// apps/web/src/wizard/schema.ts — Phase 4 plan 04-03.
//
// Validation helpers for the A4..A6 wizard inputs. The plan calls
// for `zod`, but the threat-model section explicitly forbids new
// npm packages ("The 04-03 task does NOT install any new npm
// packages"). The validation logic is small enough — and the
// i18n key mapping is the real value — to ship as a hand-rolled
// helper that mirrors the server's zod schema behavior:
//   - a4SlotSelectionSchema: validate the picked slot (defense in
//     depth — the picker only emits well-formed rows).
//   - a5GuestSchema: name required + at least one of email/phone.
//   - a5ConsentSchema: the consent text + the timestamp + the
//     `accepted: true` literal that the server requires (DSG-05).
//   - createReservationInputSchema: the wire shape the server
//     expects; mirrors `apps/api/src/graphql/schema.ts` line 647.
//
// The function returns the i18n key string (e.g. "wizard.a5.errors
// .nameRequired") so the caller can `t(key)` to localize; the
// server's `errors.messageKey` is read separately for the
// `createReservation` failure path (OQ-7 / Pitfall: never inline
// the message text in the component).

export interface ValidationIssue {
  path: string;
  key: string;
}

export interface ValidationResult {
  ok: boolean;
  issues: ValidationIssue[];
}

// ===== A4 slot selection =====

export interface SlotSelection {
  slotDefinitionId: string;
  startsAt: string;
  durationMinutes: number;
}

export function validateSlotSelection(input: unknown): ValidationResult {
  const issues: ValidationIssue[] = [];
  if (!input || typeof input !== "object") {
    issues.push({ path: "slot", key: "wizard.a4.errors.empty" });
    return { ok: false, issues };
  }
  const x = input as Record<string, unknown>;
  if (
    typeof x.slotDefinitionId !== "string" ||
    x.slotDefinitionId.length === 0
  ) {
    issues.push({
      path: "slotDefinitionId",
      key: "wizard.a4.errors.invalid",
    });
  }
  if (typeof x.startsAt !== "string" || x.startsAt.length === 0) {
    issues.push({
      path: "startsAt",
      key: "wizard.a4.errors.invalid",
    });
  }
  if (typeof x.durationMinutes !== "number" || x.durationMinutes <= 0) {
    issues.push({
      path: "durationMinutes",
      key: "wizard.a4.errors.invalid",
    });
  }
  return { ok: issues.length === 0, issues };
}

// ===== A5 guest form =====

export interface GuestFormInput {
  name: string;
  email: string;
  phone: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+0-9\s()-]{6,32}$/;

export function validateGuestForm(input: GuestFormInput): ValidationResult {
  const issues: ValidationIssue[] = [];
  if (typeof input.name !== "string" || input.name.trim().length === 0) {
    issues.push({
      path: "name",
      key: "wizard.a5.errors.nameRequired",
    });
  } else if (input.name.length > 120) {
    issues.push({
      path: "name",
      key: "wizard.a5.errors.nameTooLong",
    });
  }
  const email = input.email?.trim() ?? "";
  const phone = input.phone?.trim() ?? "";
  if (email.length === 0 && phone.length === 0) {
    // Per BOOK-02: at least one of email/phone is required.
    // Surface the error on the email field (the contact-form
    // renders a single caption at the bottom of the group).
    issues.push({
      path: "email",
      key: "wizard.a5.errors.contactRequired",
    });
  }
  if (email.length > 0 && !EMAIL_REGEX.test(email)) {
    issues.push({
      path: "email",
      key: "wizard.a5.errors.emailInvalid",
    });
  }
  if (phone.length > 0 && !PHONE_REGEX.test(phone)) {
    issues.push({
      path: "phone",
      key: "wizard.a5.errors.phoneInvalid",
    });
  }
  return { ok: issues.length === 0, issues };
}

// ===== A5 consent capture =====

export interface ConsentCapture {
  consentText: string;
  consentAt: string;
  accepted: boolean;
}

export function validateConsent(capture: ConsentCapture): ValidationResult {
  const issues: ValidationIssue[] = [];
  if (
    typeof capture.consentText !== "string" ||
    capture.consentText.length === 0
  ) {
    issues.push({
      path: "consentText",
      key: "wizard.a5.errors.consentEmpty",
    });
  }
  if (capture.accepted !== true) {
    issues.push({
      path: "accepted",
      key: "wizard.a5.errors.consentRequired",
    });
  }
  if (
    typeof capture.consentAt !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(capture.consentAt)
  ) {
    issues.push({
      path: "consentAt",
      key: "wizard.a5.errors.consentEmpty",
    });
  }
  return { ok: issues.length === 0, issues };
}

// ===== A6 createReservation input =====

export interface CreateReservationInput {
  serviceDate: string;
  slotDefinitionId: string;
  startsAt: string;
  durationMinutes: number;
  partySize: number;
  areaId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  consentText: string;
  consentAt: string;
}

const SERVICE_DATE_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function buildCreateReservationInput(args: {
  serviceDate: string;
  slotDefinitionId: string;
  startsAt: string;
  durationMinutes: number;
  partySize: number;
  areaId: string | null;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  consentText: string;
  consentAt: string;
}): ValidationResult & { value?: CreateReservationInput } {
  const issues: ValidationIssue[] = [];
  if (!SERVICE_DATE_REGEX.test(args.serviceDate)) {
    issues.push({ path: "serviceDate", key: "wizard.a6.errors.invalidDate" });
  }
  if (
    typeof args.slotDefinitionId !== "string" ||
    args.slotDefinitionId.length === 0
  ) {
    issues.push({
      path: "slotDefinitionId",
      key: "wizard.a6.errors.invalidSlot",
    });
  }
  if (typeof args.startsAt !== "string" || args.startsAt.length === 0) {
    issues.push({
      path: "startsAt",
      key: "wizard.a6.errors.invalidSlot",
    });
  }
  if (typeof args.durationMinutes !== "number" || args.durationMinutes <= 0) {
    issues.push({
      path: "durationMinutes",
      key: "wizard.a6.errors.invalidSlot",
    });
  }
  if (typeof args.partySize !== "number" || args.partySize <= 0) {
    issues.push({ path: "partySize", key: "wizard.a6.errors.partySize" });
  }
  if (args.areaId !== null && !UUID_REGEX.test(args.areaId ?? "")) {
    issues.push({ path: "areaId", key: "wizard.a6.errors.invalidArea" });
  }
  if (
    typeof args.guestName !== "string" ||
    args.guestName.trim().length === 0
  ) {
    issues.push({ path: "name", key: "wizard.a5.errors.nameRequired" });
  }
  if (typeof args.consentText !== "string" || args.consentText.length === 0) {
    issues.push({
      path: "consentText",
      key: "wizard.a5.errors.consentEmpty",
    });
  }
  if (issues.length > 0) {
    return { ok: false, issues };
  }
  const email = args.guestEmail.trim();
  const phone = args.guestPhone.trim();
  return {
    ok: true,
    issues: [],
    value: {
      serviceDate: args.serviceDate,
      slotDefinitionId: args.slotDefinitionId,
      startsAt: args.startsAt,
      durationMinutes: args.durationMinutes,
      partySize: args.partySize,
      areaId: args.areaId,
      name: args.guestName.trim(),
      email: email.length === 0 ? null : email,
      phone: phone.length === 0 ? null : phone,
      consentText: args.consentText,
      consentAt: args.consentAt,
    },
  };
}
